/** The itinerary's fact band (plan §5 "Band", §2 "Fact band"): five cells of facts, not big
 *  numbers — a title, a datum line and a muted detail line — each leading to what owns its
 *  facts, and the edge states the page shows above the band (O9). Pure: `now` is a
 *  parameter.
 *
 *  | Cell       | Datum                          | Detail                                       | Leads to        |
 *  |------------|--------------------------------|----------------------------------------------|-----------------|
 *  | In the app | the status chip's word         | the verdict's rest: the searches / why not … | Visibility card |
 *  |            |  ("Live", "Placeholder", …)    |                                              |                 |
 *  | Trip       | "12 days" (the spans' sum)     | “12 Days” · from $1,290 pp                   | Days card       |
 *  | App button | `ctaVerdict`                   | the URL's host / the tour, or the info note  | App button card |
 *  | Used by    | "2 tours cross-selling it"     | "3 tours using it as a template · 3 tours    | the Usage tab   |
 *  |            |  (cheap)                       |  built on it · 41 trips added from it"       |                 |
 *  |            |                                |  (summary + bounded counts)                  |                 |
 *  | Source     | `catalogSourceLabel`           | "days matched by title on sync" (imported)   | Identity card   |
 *
 *  The used-by summary (D7) is read lazily, after the detail:
 *  `GET itineraries/<pk>/used_by/summary/` (`usedBySummary`); it adds the template tours. Until
 *  it arrives the Used by detail holds the detail's bounded counts alone; when the database
 *  cancelled its read, "Template tours not counted (timed out)" leads them. Above 500 tours the
 *  template count may read "500+".
 *  `trips_added` stops at 1,000; `trips_added_capped` reads "1,000+". Every count is worded as
 *  a refused delete lists it (`dependentPhrase`). */
import {
  CTA_BUTTON_BEHAVIOR,
  type ItineraryDetail,
  type ItineraryUsage,
  type ItineraryUsedBySummary,
} from '@/api/itineraries';
import {
  CATALOG_STATUS_LABEL,
  catalogSourceLabel,
  ctaVerdict,
  liveVerdict,
  NO_CATALOG_ITINERARY,
  notCounted,
  PLACEHOLDER_VERDICT,
  type ItineraryCtaFacts,
  type ItineraryLiveFacts,
  type LiveVerdict,
} from '@/features/catalog/words';

import type { RecordEditorMode } from '@/features/records/editor/types';

import { dependentPhrase } from '../../words';

import { feedMatchesDaysByTitle } from '../days/daysModel';

import { tripDayCount } from './itineraryDays';

/** The itinerary record's page tabs. */
export type ItineraryRecordTab = 'itinerary' | 'images' | 'usage' | 'history';

/** The record's cards a band cell can lead to. */
export type ItineraryCardKey = 'app_button' | 'days' | 'visibility' | 'identity';

export type ItineraryBandTarget =
  | { kind: 'tab'; tab: ItineraryRecordTab }
  | { kind: 'card'; card: ItineraryCardKey };

export type ItineraryBandCellId = 'live' | 'trip' | 'cta' | 'used' | 'source';

export interface ItineraryBandCell {
  id: ItineraryBandCellId;
  title: string;
  datum: string;
  detail: string | null;
  /** The detail is an info note (a stored combination the app cannot act on): an info icon
   *  and words, never red. */
  note?: boolean;
  target: ItineraryBandTarget;
}

const count = (n: number) => n.toLocaleString('en-US');
const plural = (n: number, one: string, many = `${one}s`) => `${count(n)} ${n === 1 ? one : many}`;

/** "#31", "#31 and #73", "#31, #73 and #90". */
function joinAnd(words: string[]): string {
  if (words.length <= 1) return words.join('');
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

// ---------------------------------------------------------------------------
// The shared words' inputs
// ---------------------------------------------------------------------------

export function liveFacts(
  d: Pick<
    ItineraryDetail,
    | 'published'
    | 'placeholder'
    | 'default_itinerary'
    | 'show_in_catalog'
    | 'show_in_region_search'
    | 'show_in_trip_type_search'
    | 'show_in_text_search'
  >,
): ItineraryLiveFacts {
  return {
    published: d.published,
    placeholder: d.placeholder,
    has_default: d.default_itinerary.id !== null,
    show_in_catalog: d.show_in_catalog,
    show_in_region_search: d.show_in_region_search,
    show_in_trip_type_search: d.show_in_trip_type_search,
    show_in_text_search: d.show_in_text_search,
  };
}

export function ctaFacts(d: ItineraryDetail): ItineraryCtaFacts {
  return {
    cta_button_text: d.cta_button_text,
    cta_button_behavior: d.cta_button_behavior,
    booking_url: d.booking_url,
    cta_tour: d.cta_tour ? { name: d.cta_tour.name?.trim() || `Tour #${d.cta_tour.id}` } : null,
    cta_tour_departure: d.cta_tour_departure,
  };
}

const VERDICT_SEPARATORS = [' · ', ' — ', ': '] as const;

/** A verdict sentence as a datum and a detail line, split at its first separator:
 *  "Live · catalog and text search" → "Live" + "catalog and text search"; "Not published —
 *  hidden in the app" → "Not published" + "hidden in the app". The words stay the shared
 *  module's; only the line break is the band's. */
export function splitVerdict(text: string): { head: string; tail: string | null } {
  let at = -1;
  let sep = '';
  for (const s of VERDICT_SEPARATORS) {
    const i = text.indexOf(s);
    if (i > 0 && (at < 0 || i < at)) {
      at = i;
      sep = s;
    }
  }
  if (at < 0) return { head: text, tail: null };
  return { head: text.slice(0, at), tail: text.slice(at + sep.length) || null };
}

// ---------------------------------------------------------------------------
// Cells
// ---------------------------------------------------------------------------

/** The live verdict over the cell's two lines: the status chip's word as the datum — "Live",
 *  "Coming soon", "Not published", "Placeholder" — and the rest of the sentence as the detail
 *  ("Placeholder for new tours — not shown in the app" → "Placeholder" + "for new tours — not
 *  shown in the app"), so the chip and the band name the state alike. */
export function liveVerdictLines(verdict: LiveVerdict): { head: string; tail: string | null } {
  const word = CATALOG_STATUS_LABEL[verdict.status];
  if (!verdict.text.startsWith(word)) return splitVerdict(verdict.text);
  const rest = verdict.text.slice(word.length).replace(/^\s*(?:·|—|:)?\s*/, '');
  return { head: word, tail: rest || null };
}

function liveCell(d: ItineraryDetail): ItineraryBandCell {
  const { head, tail } = liveVerdictLines(liveVerdict(liveFacts(d)));
  return {
    id: 'live',
    title: 'In the app',
    datum: head,
    detail: tail,
    target: { kind: 'card', card: 'visibility' },
  };
}

/** "from $1,290 pp", "€85.50", "1,290 NOK per person"; null without a price. */
export function priceSummary(
  d: Pick<ItineraryDetail, 'cost' | 'cost_prefix' | 'cost_unit' | 'currency'>,
): string | null {
  const raw = d.cost?.trim();
  const n = raw ? Number(raw) : NaN;
  if (!Number.isFinite(n)) return null;
  const amount = n.toLocaleString(
    'en-US',
    Number.isInteger(n)
      ? { maximumFractionDigits: 0 }
      : { minimumFractionDigits: 2, maximumFractionDigits: 2 },
  );
  const symbol = d.currency?.symbol?.trim();
  const code = d.currency?.code?.trim();
  const money = symbol ? `${symbol}${amount}` : code ? `${amount} ${code}` : amount;
  return [d.cost_prefix?.trim(), money, d.cost_unit?.trim()].filter(Boolean).join(' ');
}

function tripCell(d: ItineraryDetail): ItineraryBandCell {
  const days = tripDayCount(d.days);
  const display = d.duration_display?.trim();
  return {
    id: 'trip',
    title: 'Trip',
    datum: d.days.length ? plural(days, 'day') : 'No days',
    detail: [display ? `“${display}”` : null, priceSummary(d) ?? 'no price']
      .filter(Boolean)
      .join(' · '),
    target: { kind: 'card', card: 'days' },
  };
}

/** The host a link button opens ("www.intrepidtravel.com"); the stored text when it is not
 *  a URL. */
function urlHost(url: string): string {
  try {
    return new URL(url).host || url;
  } catch {
    return url;
  }
}

/** What the button acts on: the booking URL's host for the link behaviours, the tour for
 *  the My Trips / Explore ones; null for the others (the number and the chat are the
 *  verdict's own words). */
function ctaTarget(behavior: number, d: ItineraryDetail): string | null {
  switch (behavior) {
    case CTA_BUTTON_BEHAVIOR.LINK_INAPP:
    case CTA_BUTTON_BEHAVIOR.LINK_BROWSER: {
      const url = d.booking_url?.trim();
      return url ? urlHost(url) : null;
    }
    case CTA_BUTTON_BEHAVIOR.ADD_TRIP:
    case CTA_BUTTON_BEHAVIOR.EXPLORE:
    case CTA_BUTTON_BEHAVIOR.ADD_OR_EXPLORE:
      return ctaFacts(d).cta_tour?.name ?? null;
    default:
      return null;
  }
}

function ctaCell(d: ItineraryDetail): ItineraryBandCell {
  const verdict = ctaVerdict(ctaFacts(d), d.operator_cta_default);
  const behavior = d.cta_button_behavior ?? d.operator_cta_default;
  const hasButton = !!d.cta_button_text?.trim();
  const target = hasButton && behavior !== null ? ctaTarget(behavior, d) : null;
  return {
    id: 'cta',
    title: 'App button',
    datum: verdict.text,
    detail: verdict.note ?? target,
    ...(verdict.note ? { note: true } : {}),
    target: { kind: 'card', card: 'app_button' },
  };
}

/** The cheap counts: "2 tours cross-selling it · 1 other itinerary sharing its days". */
export function usedByCounts(
  usage: Pick<ItineraryUsage, 'crosssold_on' | 'sharing_groups'>,
): string {
  const parts = [
    usage.crosssold_on ? dependentPhrase('crosssell_tours', usage.crosssold_on) : null,
    usage.sharing_groups ? dependentPhrase('sharing_groups', usage.sharing_groups) : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : 'Not cross-sold';
}

/** The tours and trips: "3 tours using it as a template · 3 tours built on it · 41 trips
 *  added from it", from the summary (once read) and the detail's bounded counts. Without the
 *  summary nothing counts the template tours, so "none" names only what was counted; a
 *  cancelled read says so first. A capped summary counts at most 500 template tours: all 500
 *  read "500+". */
export function usedByReach(
  usage: Pick<ItineraryUsage, 'tour_itineraries' | 'trips_added' | 'trips_added_capped'>,
  summary: ItineraryUsedBySummary | null | undefined,
): string {
  const template = summary?.available ? summary.template_tours : null;
  const templateCapped = !!summary?.capped && template !== null && template === summary.tours;
  const parts = [
    summary && template === null ? notCounted('Template tours', summary.reason) : null,
    template ? dependentPhrase('template_tours', template, templateCapped) : null,
    usage.tour_itineraries ? dependentPhrase('tour_itineraries', usage.tour_itineraries) : null,
    usage.trips_added
      ? dependentPhrase('trips_added', usage.trips_added, usage.trips_added_capped)
      : null,
  ].filter(Boolean);
  if (parts.length) return parts.join(' · ');
  return template === null ? 'No tours built on it or trips added' : 'No tours or trips added';
}

function usedCell(
  d: ItineraryDetail,
  summary: ItineraryUsedBySummary | null | undefined,
): ItineraryBandCell {
  return {
    id: 'used',
    title: 'Used by',
    datum: usedByCounts(d.usage),
    detail: usedByReach(d.usage, summary),
    target: { kind: 'tab', tab: 'usage' },
  };
}

function sourceCell(d: ItineraryDetail, now: Date): ItineraryBandCell {
  const s = d.source;
  return {
    id: 'source',
    title: 'Source',
    datum: catalogSourceLabel(s, now),
    // F44: a re-sync matches days by title (a renamed day is deleted and re-created).
    detail: feedMatchesDaysByTitle(s) ? 'days matched by title on sync' : null,
    target: { kind: 'card', card: 'identity' },
  };
}

/** The band's cells, in order. `summary` is the used-by summary once it is read (D7). */
export function itineraryFactBandCells(
  d: ItineraryDetail,
  {
    summary = null,
    now = new Date(),
  }: { summary?: ItineraryUsedBySummary | null; now?: Date } = {},
): ItineraryBandCell[] {
  return [liveCell(d), tripCell(d), ctaCell(d), usedCell(d, summary), sourceCell(d, now)];
}

// ---------------------------------------------------------------------------
// Edge states (O9)
// ---------------------------------------------------------------------------

export type ItineraryNoticeId = 'placeholder' | 'no_default' | 'shared_default';

export interface ItineraryNotice {
  id: ItineraryNoticeId;
  severity: 'info' | 'warning';
  text: string;
  /** shared_default: the other groups whose catalog itinerary this is. */
  ids?: number[];
}

/** What creates a missing catalog itinerary, in one wording wherever the page says it. In edit
 *  mode the page's own save does (plan §5, O9). In view mode nothing on the page saves yet, so
 *  it is the dashboard's: any save there creates it. Null: nothing this operator can reach. */
function catalogItineraryCreator(
  d: Pick<ItineraryDetail, 'dashboard_url'>,
  mode: RecordEditorMode,
): string | null {
  if (mode === 'edit') return 'Saving any trip field creates it.';
  return d.dashboard_url
    ? 'Saving the itinerary in the dashboard creates it. Use Actions › Open in dashboard.'
    : null;
}

function withCreator(
  head: string,
  d: Pick<ItineraryDetail, 'dashboard_url'>,
  mode: RecordEditorMode,
): string {
  const creator = catalogItineraryCreator(d, mode);
  return creator ? `${head} ${creator}` : head;
}

/** The "no catalog itinerary" banner: "Coming soon", and what creates one. */
export function noCatalogItineraryText(d: Pick<ItineraryDetail, 'dashboard_url'>, mode: RecordEditorMode): string {
  return withCreator(`${NO_CATALOG_ITINERARY}: the app shows “Coming soon”.`, d, mode);
}

/** The Images tab without a catalog itinerary: the gallery is the catalog itinerary's (the
 *  server answers its images 404 until one exists), so there is none to show or add to — and
 *  what creates one, in the banner's words. */
export function noCatalogItineraryImagesText(
  d: Pick<ItineraryDetail, 'dashboard_url'>,
  mode: RecordEditorMode,
): string {
  return withCreator(`${NO_CATALOG_ITINERARY}: images belong to it.`, d, mode);
}

/** The banners of the record: a placeholder (F41), no catalog itinerary (F42), a catalog
 *  itinerary shared with other groups (F43) — in that order, each when it applies. "No
 *  catalog itinerary" applies only while the app shows "Coming soon" (the status chip's
 *  `coming_soon`): a group that is not published, or a placeholder, is not in the app at all,
 *  so its banner would say what the app does not do. `mode`: the page's (what creates a
 *  missing catalog itinerary, `noCatalogItineraryText`). */
export function itineraryNotices(
  d: ItineraryDetail,
  { mode = 'view' }: { mode?: RecordEditorMode } = {},
): ItineraryNotice[] {
  const out: ItineraryNotice[] = [];
  if (d.placeholder) {
    out.push({
      id: 'placeholder',
      severity: 'info',
      text: PLACEHOLDER_VERDICT,
    });
  }
  if (liveVerdict(liveFacts(d)).status === 'coming_soon') {
    out.push({
      id: 'no_default',
      severity: 'info',
      text: noCatalogItineraryText(d, mode),
    });
  }
  const others = [...new Set(d.default_itinerary.shared_with)].filter((id) => id !== d.id);
  if (d.default_itinerary.id !== null && others.length) {
    const refs = joinAnd(others.map((id) => `#${id}`));
    out.push({
      id: 'shared_default',
      severity: 'warning',
      text: `Shares its catalog itinerary with ${others.length === 1 ? 'itinerary' : 'itineraries'} ${refs}: saving a trip field also changes ${refs}`,
      ids: others,
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Delete (gated up front by what the detail and the summary count)
// ---------------------------------------------------------------------------

/** Why Delete is disabled up front: what the detail counts (tours built on it, trips added
 *  from it, other itineraries sharing its days, tours cross-selling it) and, once read, the
 *  summary's template tours — each refuses the delete on the server (`409 itinerary_in_use`),
 *  in the refusal's own words and order, then its way out (unpublishing) while the itinerary
 *  is published. Null when nothing counted holds it: the server may still refuse it (a
 *  template tour the summary has not counted yet). */
export function itineraryDeleteBlockedReason(
  d: Pick<ItineraryDetail, 'usage' | 'published'>,
  summary: ItineraryUsedBySummary | null | undefined,
): string | null {
  const { usage } = d;
  const template = summary?.available ? (summary.template_tours ?? 0) : 0;
  const templateCapped = !!summary?.capped && template > 0 && template === summary.tours;
  const parts = [
    usage.tour_itineraries ? dependentPhrase('tour_itineraries', usage.tour_itineraries) : null,
    usage.trips_added ? dependentPhrase('trips_added', usage.trips_added, usage.trips_added_capped) : null,
    template ? dependentPhrase('template_tours', template, templateCapped) : null,
    usage.sharing_groups ? dependentPhrase('sharing_groups', usage.sharing_groups) : null,
    usage.crosssold_on ? dependentPhrase('crosssell_tours', usage.crosssold_on) : null,
  ].filter(Boolean);
  if (!parts.length) return null;
  // The way out the refusal names, while there is one to take.
  return `In use: ${parts.join(' · ')}.${d.published ? ' Unpublish it to hide it instead.' : ''}`;
}

